import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentMinusIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  ReceiptRefundIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import type { ParcelOperation } from '../../../types/parcels';

/** Assinatura dos ícones do Hero Icons — todos compartilham este tipo. */
type HeroIcon = typeof BanknotesIcon;

interface OperationItem {
  readonly operation: ParcelOperation;
  readonly labelKey: string;
  readonly icon: HeroIcon;
  readonly isDestructive?: boolean;
}

interface OperationGroup {
  readonly titleKey: string;
  readonly items: readonly OperationItem[];
}

/**
 * As 20 operações do menu da parcela, agrupadas como no frame `Jp0HA` de
 * `design/payment.pen`.
 *
 * Uma lista corrida é impossível de varrer; os grupos separam o que mexe em
 * cobrança, no ciclo da parcela, na integração com o provedor, na nota fiscal e
 * no histórico. Cada linha exibe só o subconjunto que
 * `isParcelOperationAvailable` libera para o status dela, então o menu real é
 * bem menor do que esta lista.
 *
 * `labelKey` e `titleKey` são literais (`as const`) para que `t()` valide cada
 * chave contra o JSON `pt-BR` em tempo de compilação.
 */
export const PARCEL_OPERATION_GROUPS = [
  {
    titleKey: 'parcels.operations.groups.payment',
    items: [
      { operation: 'pay', labelKey: 'parcels.operations.pay', icon: BanknotesIcon },
      { operation: 'edit', labelKey: 'parcels.operations.edit', icon: PencilSquareIcon },
      {
        operation: 'generateBillet',
        labelKey: 'parcels.operations.generateBillet',
        icon: ArrowDownTrayIcon,
      },
      {
        operation: 'sendCharge',
        labelKey: 'parcels.operations.sendCharge',
        icon: CreditCardIcon,
      },
      { operation: 'sendEmail', labelKey: 'parcels.operations.sendEmail', icon: EnvelopeIcon },
      {
        operation: 'reversePayment',
        labelKey: 'parcels.operations.reversePayment',
        icon: ArrowUturnLeftIcon,
      },
      {
        operation: 'refundPayment',
        labelKey: 'parcels.operations.refundPayment',
        icon: ReceiptRefundIcon,
        isDestructive: true,
      },
    ],
  },
  {
    titleKey: 'parcels.operations.groups.lifecycle',
    items: [
      { operation: 'activate', labelKey: 'parcels.operations.activate', icon: CheckCircleIcon },
      {
        operation: 'cancel',
        labelKey: 'parcels.operations.cancel',
        icon: XCircleIcon,
        isDestructive: true,
      },
    ],
  },
  {
    titleKey: 'parcels.operations.groups.integration',
    items: [
      {
        operation: 'reintegrate',
        labelKey: 'parcels.operations.reintegrate',
        icon: ArrowPathIcon,
      },
      {
        operation: 'resetIntegration',
        labelKey: 'parcels.operations.resetIntegration',
        icon: ArrowDownTrayIcon,
      },
      {
        operation: 'takeAdvantageParcel',
        labelKey: 'parcels.operations.takeAdvantageParcel',
        icon: ArrowsRightLeftIcon,
      },
    ],
  },
  {
    titleKey: 'parcels.operations.groups.invoice',
    items: [
      {
        operation: 'openInvoice',
        labelKey: 'parcels.operations.openInvoice',
        icon: ArrowTopRightOnSquareIcon,
      },
      {
        operation: 'invoiceFuturePayment',
        labelKey: 'parcels.operations.invoiceFuturePayment',
        icon: CalendarDaysIcon,
      },
      {
        operation: 'invoiceFuturePaymentVanzolini',
        labelKey: 'parcels.operations.invoiceFuturePaymentVanzolini',
        icon: CalendarDaysIcon,
      },
      {
        operation: 'cancelInvoice',
        labelKey: 'parcels.operations.cancelInvoice',
        icon: DocumentMinusIcon,
        isDestructive: true,
      },
    ],
  },
  {
    titleKey: 'parcels.operations.groups.history',
    items: [
      {
        operation: 'traceability',
        labelKey: 'parcels.operations.traceability',
        icon: ClockIcon,
      },
      {
        operation: 'orderTracking',
        labelKey: 'parcels.operations.orderTracking',
        icon: TruckIcon,
      },
    ],
  },
] as const satisfies readonly OperationGroup[];
